/**
 * One screen that gives any declared resource a full editing surface: searchable,
 * filterable, paged list; a create/edit drawer; deactivate, restore and delete.
 *
 * The server is the authority on every rule. This page never pre-judges a write —
 * it sends it and shows whatever the server says back, because the message the
 * server returns is the one that names the actual reason.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, ApiError, session } from '../lib/api'
import { useApi } from '../lib/hooks'
import { date as fmtDate, money, num, titleCase } from '../lib/format'
import { Banner, Empty, ErrorNote, IconSearch, Loading, Panel, Pill, StatMini } from './kit'
import { BackLink } from '../console/breadcrumbs'

// ---------------------------------------------------------------- definitions
export type FieldType =
  | 'text' | 'textarea' | 'number' | 'decimal' | 'money' | 'bool'
  | 'select' | 'ref' | 'date' | 'time'

export interface FieldDef {
  name: string
  label: string
  type?: FieldType
  required?: boolean
  readOnly?: boolean
  hint?: string
  /** Fixed options for a select. */
  options?: Array<{ value: string; label: string }>
  /** Options loaded from another resource. */
  ref?: { resource: string; labelField?: string; filterBy?: string }
  step?: number
  suffix?: string
  /**
   * Value a new record starts with. Without this, a boolean would seed to false and
   * a row created with isActive false disappears from its own list the moment it is
   * saved - which reads as "it did not save".
   */
  default?: unknown
}

export interface ResourceDef {
  key: string
  title: string
  singular: string
  description: string
  fields: FieldDef[]
  /** Field names, in order, for the table. */
  columns: string[]
  /** Roles allowed to write. Read is open to any signed-in user. */
  writeRoles: string[]
  searchable?: boolean
  filters?: Array<{ name: string; label: string; options?: Array<{ value: string; label: string }>; ref?: { resource: string; labelField?: string } }>
  /** Extra computed columns the list endpoint returns. */
  derived?: Array<{ name: string; label: string }>
  /** Rows with is_active can be deactivated rather than deleted. */
  hasActive?: boolean
  note?: string
}

type Row = Record<string, unknown>

interface ListResponse { items: Row[]; page: number; pageSize: number; total: number }

// ---------------------------------------------------------------- lookups
const lookupCache = new Map<string, Array<{ value: string; label: string }>>()

function useLookup(ref?: FieldDef['ref']) {
  const [options, setOptions] = useState<Array<{ value: string; label: string }>>(
    ref ? lookupCache.get(ref.resource) ?? [] : [],
  )

  useEffect(() => {
    if (!ref) return
    const cached = lookupCache.get(ref.resource)
    if (cached) { setOptions(cached); return }

    let cancelled = false
    void api
      // Some endpoints paginate and some return a bare array; accept either.
      .get<ListResponse | Row[]>(`/${ref.resource}?pageSize=500`)
      .then((res) => {
        const items = Array.isArray(res) ? res : res.items ?? []
        const labelField = ref.labelField ?? 'name'
        const opts = items.map((i) => ({
          value: String(i.id ?? i.code ?? ''),
          label: String(i[labelField] ?? i.name ?? i.code ?? ''),
        }))
        lookupCache.set(ref.resource, opts)
        if (!cancelled) setOptions(opts)
      })
      .catch(() => { /* a lookup that fails leaves a free-text id, not a broken page */ })
    return () => { cancelled = true }
  }, [ref?.resource, ref?.labelField])

  return options
}

/** Clears cached dropdown options after an edit so new rows appear elsewhere. */
export function invalidateLookups(resource?: string) {
  if (resource) lookupCache.delete(resource)
  else lookupCache.clear()
}

// ---------------------------------------------------------------- page
export function ResourcePage({ def, back }: {
  def: ResourceDef
  /** Where this list was opened from, so there is always a way out of it. */
  back?: { to: string; label: string }
}) {
  const [q, setQ] = useState('')
  const [filters, setFilters] = useState<Record<string, string>>({})
  const [page, setPage] = useState(1)
  const [includeInactive, setIncludeInactive] = useState(false)
  const [editing, setEditing] = useState<Row | 'new' | null>(null)
  const [banner, setBanner] = useState<{ tone: 'ok' | 'crit'; text: string } | null>(null)

  const canWrite = def.writeRoles.some((r) => session.has(r))

  const query = useMemo(() => {
    const p = new URLSearchParams({ page: String(page), pageSize: '50' })
    if (q.trim()) p.set('q', q.trim())
    if (includeInactive) p.set('includeInactive', 'true')
    for (const [k, v] of Object.entries(filters)) if (v) p.set(k, v)
    return p.toString()
  }, [q, filters, page, includeInactive])

  const list = useApi<ListResponse>(`/${def.key}?${query}`, { deps: [query] })

  const refresh = useCallback(() => {
    invalidateLookups(def.key)
    list.reload()
  }, [def.key, list])

  async function remove(row: Row) {
    const label = String(row.name ?? row.code ?? row.fullName ?? 'this record')
    const soft = def.hasActive
    const ok = window.confirm(
      soft
        ? `Deactivate ${label}?\n\nIt stays on past records and can be restored.`
        : `Delete ${label}?\n\nThis cannot be undone.`,
    )
    if (!ok) return
    try {
      const res = await api.del<{ message: string }>(`/${def.key}/${row.id ?? row.code}`)
      setBanner({ tone: 'ok', text: res.message })
      refresh()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'Could not delete that.' })
    }
  }

  async function restore(row: Row) {
    try {
      await api.post(`/${def.key}/${row.id ?? row.code}/restore`)
      setBanner({ tone: 'ok', text: `${String(row.name ?? row.code)} was restored.` })
      refresh()
    } catch (e) {
      setBanner({ tone: 'crit', text: e instanceof ApiError ? e.message : 'Could not restore that.' })
    }
  }

  const rows = list.data?.items ?? []
  const total = list.data?.total ?? 0
  const pages = Math.max(1, Math.ceil(total / (list.data?.pageSize ?? 50)))

  return (
    <main className="page">
      {back && <BackLink to={back.to} label={back.label} />}

      <div className="page-head spread">
        <div>
          <h1>{def.title}</h1>
          <p>{def.description}</p>
        </div>
        {canWrite && (
          <button className="btn" onClick={() => setEditing('new')}>
            + New {def.singular.toLowerCase()}
          </button>
        )}
      </div>

      <div className="stack">
        {banner && (
          <Banner tone={banner.tone === 'ok' ? 'info' : 'crit'} title={banner.text}>
            {null}
          </Banner>
        )}
        {def.note && <Banner tone="info" title={def.note}>{null}</Banner>}
        {!canWrite && (
          <Banner tone="info" title="You have read-only access here">
            Editing {def.title.toLowerCase()} needs the {def.writeRoles.join(' or ')} role.
          </Banner>
        )}

        <Panel
          title={`${total} ${total === 1 ? def.singular.toLowerCase() : def.title.toLowerCase()}`}
          meta={
            <div className="row" style={{ gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {def.searchable !== false && (
                <div className="row" style={{ gap: 6, border: '1px solid var(--line)', borderRadius: 'var(--r-sm)', padding: '0 8px', background: 'var(--surface)' }}>
                  <IconSearch size={14} />
                  <input
                    className="input"
                    style={{ border: 'none', minHeight: 28, padding: '4px 0', width: 180, background: 'transparent' }}
                    placeholder="Search"
                    value={q}
                    onChange={(e) => { setQ(e.target.value); setPage(1) }}
                  />
                </div>
              )}
              {def.filters?.map((f) => (
                <FilterSelect
                  key={f.name}
                  filter={f}
                  value={filters[f.name] ?? ''}
                  onChange={(v) => { setFilters((s) => ({ ...s, [f.name]: v })); setPage(1) }}
                />
              ))}
              {def.hasActive && (
                <button
                  className={`btn ${includeInactive ? '' : 'quiet'}`}
                  style={{ minHeight: 30, padding: '4px 12px', fontSize: 'var(--fs-xs)' }}
                  onClick={() => { setIncludeInactive((v) => !v); setPage(1) }}
                >
                  Show inactive
                </button>
              )}
            </div>
          }
          footer={
            pages > 1 ? (
              <div className="spread" style={{ width: '100%' }}>
                <StatMini label="Showing" value={`${rows.length} of ${total}`} />
                <div className="row" style={{ gap: 8 }}>
                  <button className="btn quiet" style={{ minHeight: 30 }} disabled={page <= 1}
                    onClick={() => setPage((p) => p - 1)}>Previous</button>
                  <span className="faint" style={{ fontSize: 'var(--fs-xs)' }}>Page {page} of {pages}</span>
                  <button className="btn quiet" style={{ minHeight: 30 }} disabled={page >= pages}
                    onClick={() => setPage((p) => p + 1)}>Next</button>
                </div>
              </div>
            ) : undefined
          }
        >
          {list.error && <ErrorNote error={list.error} onRetry={list.reload} />}
          {list.loading && !list.data && <Loading rows={6} />}
          {list.data && rows.length === 0 && (
            <Empty title={`No ${def.title.toLowerCase()} yet`}>
              {canWrite
                ? `Create the first ${def.singular.toLowerCase()} to get started.`
                : `Nothing has been set up here yet.`}
            </Empty>
          )}

          {rows.length > 0 && (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    {def.columns.map((name) => {
                      const f = def.fields.find((x) => x.name === name)
                      const d = def.derived?.find((x) => x.name === name)
                      const numeric = f && ['number', 'decimal', 'money'].includes(f.type ?? 'text')
                      return (
                        <th key={name} className={numeric || d ? 'n' : ''}>
                          {f?.label ?? d?.label ?? titleCase(name)}
                        </th>
                      )
                    })}
                    {def.hasActive && <th>Status</th>}
                    {canWrite && <th style={{ width: 130 }} />}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const inactive = def.hasActive && row.isActive === false
                    return (
                      <tr key={String(row.id ?? row.code)} className={inactive ? 'attention' : ''}>
                        {def.columns.map((name) => (
                          <Cell key={name} def={def} name={name} row={row} />
                        ))}
                        {def.hasActive && (
                          <td>
                            <Pill tone={inactive ? 'warn' : 'ok'}>{inactive ? 'Inactive' : 'Active'}</Pill>
                          </td>
                        )}
                        {canWrite && (
                          <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                            <button className="btn quiet" style={{ minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' }}
                              onClick={() => setEditing(row)}>Edit</button>
                            {' '}
                            {inactive ? (
                              <button className="btn ghost" style={{ minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)' }}
                                onClick={() => void restore(row)}>Restore</button>
                            ) : (
                              <button className="btn quiet" style={{ minHeight: 26, padding: '2px 10px', fontSize: 'var(--fs-xs)', color: 'var(--crit)' }}
                                onClick={() => void remove(row)}>
                                {def.hasActive ? 'Deactivate' : 'Delete'}
                              </button>
                            )}
                          </td>
                        )}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {editing && (
        <ResourceDrawer
          def={def}
          row={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={(msg) => {
            setEditing(null)
            setBanner({ tone: 'ok', text: msg })
            refresh()
          }}
        />
      )}
    </main>
  )
}

// ---------------------------------------------------------------- cell
function Cell({ def, name, row }: { def: ResourceDef; name: string; row: Row }) {
  const f = def.fields.find((x) => x.name === name)
  const value = row[name]

  if (value === null || value === undefined || value === '') {
    return <td className="faint">—</td>
  }

  switch (f?.type) {
    case 'bool':
      return <td>{value ? <Pill tone="ok" dot={false}>Yes</Pill> : <span className="faint">No</span>}</td>
    case 'money':
      return <td className="n">{money(value as number, { currency: false })}</td>
    case 'number':
    case 'decimal':
      return <td className="n">{num(value as number, f.step && f.step < 1 ? 2 : 0)}{f.suffix ? ` ${f.suffix}` : ''}</td>
    case 'date':
      return <td className="n">{fmtDate(value as string)}</td>
    case 'time':
      return <td className="n">{String(value).slice(0, 5)}</td>
    case 'select':
      return <td>{f.options?.find((o) => o.value === value)?.label ?? titleCase(String(value))}</td>
    case 'ref':
      return <RefCell refDef={f.ref!} value={String(value)} />
    default:
      break
  }

  if (name === 'code') return <td className="code strong">{String(value)}</td>
  if (name === 'name' || name === 'fullName') return <td className="strong truncate">{String(value)}</td>
  return <td className={typeof value === 'number' ? 'n' : 'truncate'}>{String(value)}</td>
}

function RefCell({ refDef, value }: { refDef: NonNullable<FieldDef['ref']>; value: string }) {
  const options = useLookup(refDef)
  const hit = options.find((o) => o.value === value)
  return <td className="truncate">{hit?.label ?? <span className="mono faint">{value.slice(0, 8)}</span>}</td>
}

// ---------------------------------------------------------------- filter
function FilterSelect({
  filter, value, onChange,
}: {
  filter: NonNullable<ResourceDef['filters']>[number]
  value: string
  onChange: (v: string) => void
}) {
  const refOptions = useLookup(filter.ref)
  const options = filter.options ?? refOptions
  return (
    <select className="input" style={{ minHeight: 30, width: 'auto' }} value={value}
      onChange={(e) => onChange(e.target.value)} aria-label={filter.label}>
      <option value="">All {filter.label.toLowerCase()}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  )
}

// ---------------------------------------------------------------- drawer
function ResourceDrawer({
  def, row, onClose, onSaved,
}: {
  def: ResourceDef
  row: Row | null
  onClose: () => void
  onSaved: (message: string) => void
}) {
  const isNew = row === null
  const [values, setValues] = useState<Record<string, unknown>>(() => {
    const seed: Record<string, unknown> = {}
    for (const f of def.fields) {
      if (f.readOnly) continue
      seed[f.name] = row
        ? row[f.name] ?? ''
        : f.default ?? (f.type === 'bool' ? false : '')
    }
    return seed
  })
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  async function save(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setError(null)

    // On create, a field left empty is omitted entirely so the column's own default
    // still applies — sending an explicit null would override it and trip NOT NULL.
    // On update, an explicit null is meaningful: it means "clear this".
    const body: Record<string, unknown> = {}
    for (const f of def.fields) {
      if (f.readOnly) continue
      const v = values[f.name]
      const empty = v === '' || v === null || v === undefined
      if (isNew && empty) continue
      body[f.name] = empty ? null : v
    }

    try {
      if (isNew) {
        await api.post(`/${def.key}`, body)
        onSaved(`${def.singular} created.`)
      } else {
        await api.put(`/${def.key}/${row!.id ?? row!.code}`, body)
        onSaved(`${def.singular} saved.`)
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="drawer-scrim" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <form className="drawer" onSubmit={save}>
        <header className="drawer-head">
          <div>
            <span className="micro">{isNew ? 'New' : 'Edit'}</span>
            <h3>{isNew ? def.singular : String(row?.name ?? row?.code ?? row?.fullName ?? def.singular)}</h3>
          </div>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="drawer-body">
          {error && <div className="error-text" style={{ marginBottom: 12 }}>{error}</div>}
          {def.fields.filter((f) => !f.readOnly).map((f) => (
            <Field
              key={f.name}
              def={f}
              value={values[f.name]}
              onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
            />
          ))}
        </div>

        <footer className="drawer-foot">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy}>
            {busy ? 'Saving…' : isNew ? `Create ${def.singular.toLowerCase()}` : 'Save changes'}
          </button>
        </footer>
      </form>
    </div>
  )
}

// ---------------------------------------------------------------- field
function Field({
  def, value, onChange,
}: {
  def: FieldDef
  value: unknown
  onChange: (v: unknown) => void
}) {
  const options = useLookup(def.ref)
  const id = `f-${def.name}`

  if (def.type === 'bool') {
    return (
      <label className="check-row" htmlFor={id}>
        <input id={id} type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        <span>
          <b>{def.label}</b>
          {def.hint && <i>{def.hint}</i>}
        </span>
      </label>
    )
  }

  return (
    <div className="field">
      <label htmlFor={id}>
        {def.label}
        {def.required && <span className="crit-t"> *</span>}
      </label>

      {def.type === 'textarea' ? (
        <textarea id={id} className="input" rows={3} value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)} required={def.required} />
      ) : def.type === 'select' || def.type === 'ref' ? (
        <select id={id} className="input" value={String(value ?? '')}
          onChange={(e) => onChange(e.target.value)} required={def.required}>
          <option value="">— none —</option>
          {(def.options ?? options).map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          className={`input ${['number', 'decimal', 'money'].includes(def.type ?? '') ? 'n' : ''}`}
          type={
            def.type === 'date' ? 'date'
              : def.type === 'time' ? 'time'
              : ['number', 'decimal', 'money'].includes(def.type ?? '') ? 'number'
              : 'text'
          }
          step={def.step ?? (def.type === 'decimal' || def.type === 'money' ? 0.01 : undefined)}
          value={def.type === 'time' ? String(value ?? '').slice(0, 5) : String(value ?? '')}
          onChange={(e) => onChange(e.target.value)}
          required={def.required}
        />
      )}

      {def.hint && <span className="hint">{def.hint}</span>}
    </div>
  )
}
